import { h } from "preact";
import { useState, useRef, useEffect } from "preact/hooks";
import { Trans } from "@lingui/react/macro";
import { CircleCheck, CircleXmark } from "../../../../utils/icons";
import { ManualPaymentMethod, ManualDepositFormData, ManualDepositPageComponentProps } from "./types";
import { ManualDepositService } from "./service";
import Config from "../../../../config";

function PhoneIcon() {
    return (
        <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
        </svg>
    );
}

function WarningIcon() {
    return (
        <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
    );
}

function CopyIcon() {
    return (
        <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
        </svg>
    );
}

export function ManualDepositPage(props: ManualDepositPageComponentProps) {
    const service = useRef(new ManualDepositService(Config.softionAPI));
    const [methods, setMethods] = useState<ManualPaymentMethod[]>([]);
    const [loadingMethods, setLoadingMethods] = useState(true);
    const [selectedMethodId, setSelectedMethodId] = useState<string>('');
    const [amount, setAmount] = useState<string>('');
    const [note, setNote] = useState<string>('');
    const [screenshot, setScreenshot] = useState<File | null>(null);
    const [step, setStep] = useState<'form' | 'submitting' | 'success' | 'error'>('form');
    const [error, setError] = useState<string | null>(null);
    const [noteError, setNoteError] = useState<string | null>(null);
    const fileRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        service.current.getMethods().then(methods => {
            setMethods(methods);
            if (methods.length > 0) setSelectedMethodId(methods[0].id);
            setLoadingMethods(false);
        });
    }, []);

    const selectedMethod = methods.find(m => m.id === selectedMethodId) || methods[0];

    const handleDeposit = async () => {
        const val = parseFloat(amount);
        if (isNaN(val) || val <= 0) return;
        if (!note.trim()) { setNoteError('This field is required'); return; }
        setNoteError(null);
        setStep('submitting');
        try {
            const depositId = await service.current.submitDeposit({ methodId: selectedMethodId, amount, note });
            if (screenshot) {
                await service.current.uploadScreenshot(depositId, screenshot);
            }
            setStep('success');
        } catch (err: any) {
            setError(err?.message || 'An unexpected error occurred.');
            setStep('error');
        }
    };

    const reset = () => {
        setStep('form');
        setAmount('');
        setNote('');
        setNoteError(null);
        setScreenshot(null);
        setError(null);
        if (fileRef.current) fileRef.current.value = '';
    };

    if (loadingMethods) {
        return (
            <div class="grow w-full flex flex-col items-center justify-center bg-slate-900 overflow-auto">
                <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500" />
            </div>
        );
    }

    if (step === 'submitting') {
        return (
            <div class="grow w-full flex flex-col items-center justify-center bg-slate-900 overflow-auto">
                <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500" />
            </div>
        );
    }

    if (step === 'success') {
        return (
            <div class="grow w-full flex flex-col items-center justify-center bg-slate-900 overflow-auto">
                <div class="flex flex-col items-center gap-4 p-6 text-center max-w-sm">
                    <CircleCheck class="h-32 text-green-500" />
                    <div class="text-xl font-bold text-white"><Trans>Payment Submitted</Trans></div>
                    <div class="text-base text-slate-300">
                        <Trans>Your payment notification has been sent. Our team will verify and credit your account shortly.</Trans>
                    </div>
                    <button class="px-5 py-3 rounded-lg bg-yellow-500 text-slate-900 font-bold text-xl" onClick={reset}>
                        <Trans>New Deposit</Trans>
                    </button>
                </div>
            </div>
        );
    }

    if (step === 'error') {
        return (
            <div class="grow w-full flex flex-col items-center justify-center bg-slate-900 overflow-auto">
                <div class="flex flex-col items-center gap-4 p-6 text-center max-w-sm">
                    <CircleXmark class="h-32 text-red-600" />
                    <div class="text-xl font-bold text-white"><Trans>Error</Trans></div>
                    <div class="text-base text-red-600">{error}</div>
                    <button class="px-5 py-3 rounded-lg bg-yellow-500 text-slate-900 font-bold text-xl" onClick={() => setStep('form')}>
                        <Trans>Try Again</Trans>
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div class="grow w-full flex flex-col items-center bg-slate-900 overflow-auto">
            <div class="w-full max-w-sm p-4 flex flex-col gap-5">
                <h2 class="text-2xl font-bold text-white"><Trans>Deposit</Trans></h2>

                <p class="text-sm text-slate-400 text-center -mt-2">
                    <Trans>Please select your preferred deposit method</Trans>
                </p>

                <div class="flex gap-3 justify-center">
                    {methods.map(method => {
                        const isSelected = selectedMethodId === method.id;
                        return (
                            <button
                                key={method.id}
                                class={`flex flex-col items-center gap-2 p-3 rounded-xl border-2 min-w-[90px] transition-all duration-200 ${
                                    isSelected
                                        ? 'border-yellow-500 bg-slate-700/50 shadow-lg shadow-yellow-500/10'
                                        : 'border-slate-600 bg-slate-800/50 hover:border-slate-500'
                                }`}
                                onClick={() => setSelectedMethodId(method.id)}
                            >
                                <div class={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${
                                    isSelected ? 'border-yellow-500' : 'border-slate-500'
                                }`}>
                                    {isSelected && <div class="w-2 h-2 rounded-full bg-yellow-500" />}
                                </div>
                                <span class="text-xs text-white font-medium">{method.name}</span>
                            </button>
                        );
                    })}
                </div>

                <div class="flex items-center gap-3">
                    <div class="h-px flex-1 bg-slate-700" />
                    <span class="text-yellow-500 text-xs font-semibold tracking-widest uppercase"><Trans>Manual</Trans></span>
                    <div class="h-px flex-1 bg-slate-700" />
                </div>

                <div class="flex gap-2">
                    <input
                        class="flex-1 border rounded-lg bg-slate-800 border-slate-600 text-white py-2.5 px-3 text-base placeholder-slate-500 outline-none focus:border-yellow-500/50 focus:ring-1 focus:ring-yellow-500/30 transition-all"
                        type="number"
                        value={amount}
                        onInput={e => setAmount((e.target as HTMLInputElement).value)}
                        placeholder={window.Translation('Enter Amount')}
                        min="10"
                        step="1"
                    />
                    <button
                        class="px-6 py-2.5 rounded-lg bg-yellow-500 text-slate-900 font-bold text-base hover:bg-yellow-400 active:bg-yellow-600 transition-colors"
                        onClick={handleDeposit}
                    >
                        <Trans>Deposit</Trans>
                    </button>
                </div>

                <div>
                    <textarea
                        class={`w-full border rounded-lg bg-slate-800 text-white py-2.5 px-3 text-sm placeholder-slate-500 outline-none focus:border-yellow-500/50 focus:ring-1 focus:ring-yellow-500/30 transition-all resize-none ${noteError ? 'border-red-500 focus:border-red-500 focus:ring-red-500/30' : 'border-slate-600'}`}
                        rows={2}
                        value={note}
                        onInput={e => { setNote((e.target as HTMLTextAreaElement).value); setNoteError(null); }}
                        placeholder={'SMS Message Here....ሲከፍሉ የተላከሎትን ሜሴጅ እዚህ ላይ ያስቀምጡ'}
                    />
                    {noteError ? <div class="text-red-500 text-xs mt-1">{noteError}</div> : null}
                </div>

                <div class="flex items-center gap-3">
                    <label class="flex-1 flex items-center gap-2 border border-slate-600 rounded-lg bg-slate-800 px-3 py-2.5 cursor-pointer hover:border-yellow-500/50 transition-colors">
                        <span class="text-slate-400 text-sm">{screenshot ? screenshot.name : (window.Translation('Attach screenshot (optional)'))}</span>
                        <input
                            ref={fileRef}
                            type="file"
                            accept="image/png,image/jpeg,image/gif,image/webp"
                            class="hidden"
                            onChange={e => {
                                const files = (e.target as HTMLInputElement).files;
                                setScreenshot(files && files.length > 0 ? files[0] : null);
                            }}
                        />
                    </label>
                    {screenshot ? (
                        <button
                            class="text-red-500 hover:text-red-400 text-sm shrink-0 px-1"
                            onClick={() => { setScreenshot(null); if (fileRef.current) fileRef.current.value = ''; }}
                        >✕</button>
                    ) : null}
                </div>

                {selectedMethod && (
                    <div class="bg-slate-800/80 rounded-xl p-4 border border-slate-700/50 text-slate-300 text-sm space-y-4">
                        <div>
                            <h3 class="text-base font-bold text-white">
                                <Trans>How to Deposit via</Trans> {selectedMethod.name}
                            </h3>
                            <p class="text-slate-500 text-xs mt-0.5">
                                <Trans>To deposit via</Trans> {selectedMethod.name}, <Trans>please follow these steps:</Trans>
                            </p>
                        </div>

                        <div class="bg-slate-900/50 rounded-lg p-3 -mx-1">
                            <div class="font-semibold text-white flex items-center gap-1.5 mb-1.5 text-xs uppercase tracking-wide">
                                {selectedMethod.label === 'Phone Number' ? <PhoneIcon /> : null}
                                <span>{selectedMethod.label}</span>
                            </div>
                            <div class="flex items-center gap-2">
                                <span class="text-yellow-400 font-mono text-base font-bold tabular-nums">{selectedMethod.recipient.value}</span>
                                {selectedMethod.copyable ? (
                                    <button
                                        class="text-slate-500 hover:text-yellow-400 transition-colors shrink-0"
                                        onClick={() => navigator.clipboard?.writeText(selectedMethod.recipient.value)}
                                        title="Copy"
                                    >
                                        <CopyIcon />
                                    </button>
                                ) : null}
                            </div>
                            {selectedMethod.recipient.name ? (
                                <div class="text-slate-400 text-xs mt-1">{selectedMethod.recipient.name}</div>
                            ) : null}
                        </div>

                        <div>
                            <div class="font-semibold text-white mb-1.5 text-xs uppercase tracking-wide"><Trans>Steps</Trans></div>
                            <ol class="list-decimal pl-4 text-slate-300 space-y-1 leading-relaxed">
                                {selectedMethod.steps.map((s, i) => (
                                    <li key={i}>{s}</li>
                                ))}
                            </ol>
                        </div>

                        <div class="bg-amber-900/20 border border-amber-700/30 rounded-lg p-3 -mx-1">
                            <div class="font-semibold text-amber-400 flex items-center gap-1.5 mb-1.5 text-xs uppercase tracking-wide">
                                <WarningIcon />
                                <span><Trans>Important Reminders</Trans></span>
                            </div>
                            <ol class="list-decimal pl-4 text-slate-300 space-y-1 leading-relaxed">
                                {selectedMethod.reminders.map((r, i) => (
                                    <li key={i}>{r}</li>
                                ))}
                            </ol>
                        </div>
                    </div>
                )}

                <div class="flex justify-center">
                    <button class="text-slate-500 hover:text-white text-sm transition-colors px-4 py-1" onClick={reset}>
                        <Trans>Cancel</Trans>
                    </button>
                </div>
            </div>
        </div>
    );
}
